import React, { useState } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { BsBraces } from 'react-icons/bs';

import { dataFields } from '../../BayPolotnoEditor/dataFields';
import BaySearchInput from '../../BaySearchInput';
import { sections } from '../../utilities/constants';
import './CaptionEditor.scss';

interface CustomToggleProps {
	children?: React.ReactNode;
	onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

interface VariableDropdownProps {
	baseKey: string;
	t: (key: string) => string;
	editorRef?: React.RefObject<any> | null;
	fieldOptions: (key: string, fields: any) => any[];
	onSelect: (option: any, editorRef?: any) => void;
}

const CustomToggle = React.forwardRef<HTMLButtonElement, CustomToggleProps>(({ children, onClick }, ref) => (
	<Button
		ref={ref}
		className="text-700 text-decoration-none d-flex align-items-center justify-content-center p-0 pe-2 m-0 cursor-pointer"
		variant="link"
		onClick={(e) => {
			e.preventDefault();
			e.stopPropagation();
			if (onClick) onClick(e);
		}}
	>
		<span className="me-1">
			<BsBraces />
		</span>{' '}
		{children}
	</Button>
));

CustomToggle.displayName = 'CustomToggle';

const VariableDropdown: React.FC<VariableDropdownProps> = ({ baseKey, t, editorRef = null, fieldOptions, onSelect }) => {
	const [tagSearch, setTagSearch] = useState('');
	const [filteredFields, setFilteredFields] = useState(dataFields);
	const [open, setOpen] = useState(false);

	const hasAnyResults = sections.some(({ key }) =>
		fieldOptions(key, filteredFields).length > 0,
	);

	const handleToggle = (isOpen: boolean) => {
		setOpen(isOpen);

		if (!isOpen) {
			setTagSearch('');
			setFilteredFields(dataFields);
		}
	};

	const filterMergeTags = (searchTerm: string) => {
		if (!searchTerm || !searchTerm.trim()) {
			setFilteredFields(dataFields);
			setTagSearch('');
			return;
		}

		setTagSearch(searchTerm);
		const filtered = Object.fromEntries(
			Object.entries(dataFields).filter(([, value]) => {
				return value.label.toLowerCase().includes(searchTerm.toLowerCase());
			}),
		) as typeof dataFields;
		setFilteredFields(filtered);
	};

	return (
		<Dropdown align="end" show={open} onToggle={handleToggle}>
			<Dropdown.Toggle
				as={CustomToggle}
				className="text-dark text-decoration-none p-0"
				id="dropdown-basic"
				size="sm"
				variant="link"
			>
				{t('addDynamicVariables')}
			</Dropdown.Toggle>
			<Dropdown.Menu className="overflow-x-hidden z-3 ps-2 caption-editor-dropdown-menu">
				<BaySearchInput
					className="variables-search"
					placeholder={t('startTyping')}
					showClear={false}
					value={tagSearch}
					onChange={filterMergeTags}
					onSearch={filterMergeTags}
				/>

				{!hasAnyResults && (
					<div className="text-center text-muted py-2 pb-1">
						<div className="fw-semibold fs-6 mb-3 text-dark">{t('noResultsFound')}</div>
						<span>{t('noResultText')}</span>
					</div>
				)}

				{sections.map(({ key, label }, index) => {
					const options = fieldOptions(key, filteredFields);
					if (!options.length) return null;

					return (
						<React.Fragment key={key}>
							{index !== 0 && <Dropdown.Divider />}
							<Dropdown.Header className="text-uppercase">{label}</Dropdown.Header>

							{options.map((option) => (
								<Dropdown.Item
									key={`${baseKey}-${option.value}`}
									className="ms-3 text-dark text-decoration-none"
									onClick={() =>
										editorRef ? onSelect(option, editorRef) : onSelect(option.value)
									}
								>
									{option.label}
								</Dropdown.Item>
							))}
						</React.Fragment>
					);
				})}
			</Dropdown.Menu>
		</Dropdown>
	);
};

export default VariableDropdown;
